import { Body, Box, Checkbox, Flex, Heading, Sheet } from '@hover/blueprint';
import React from 'react';
import { Trans } from 'react-i18next';

import { SpaceTypeSelector } from 'src/modules/homeownerApp/components/UploadImage/SpaceTypeSelector';
import { UploadImageBox } from 'src/modules/homeownerApp/components/UploadImage/UploadImageBox';
import { useViewportSize } from 'src/modules/homeownerApp/hooks';
import { Button, LinkExternal } from 'src/shared/components';
import { TranslationNamespace, useTranslate } from 'src/shared/hooks';
import { External } from 'src/shared/route';

type SpaceTypeSheetContentProps = {
  imageUrl: string;
  consentMandatory: boolean;
  setSpaceType: (spaceType: string) => void;
  spaceType: string;
  spaceTypes: string[];
  agreedToTerms: boolean;
  setAgreedToTerms: (value: boolean) => void;
  canContinue: boolean;
  isLoading: boolean;
  onNext: () => void;
  trackingData: object;
  ctaText: string;
  legalText?: JSX.Element;
  spaceTypeSelectorComponent?: JSX.Element;
};

export const VERTICAL_DRAWER_PADDING_PIXELS = 60;

export const SpaceTypeSheetContent = ({
  imageUrl,
  consentMandatory,
  spaceType,
  spaceTypes,
  setSpaceType,
  agreedToTerms,
  setAgreedToTerms,
  canContinue,
  isLoading,
  onNext,
  trackingData,
  ctaText,
  legalText,
  spaceTypeSelectorComponent,
}: SpaceTypeSheetContentProps) => {
  const t = useTranslate(TranslationNamespace.HOMEOWNER_APP);
  const { height: viewportHeight } = useViewportSize();

  return (
    <Sheet.Contents contentKey="space-type-selector">
      <Box
        alignItems={{ base: 'start', tablet: 'center' }}
        backgroundColor="neutral.0"
        data-testid="upload-image-box-wrapper"
        flexDirection={{ base: 'column', desktop: 'row-reverse' }}
        height={{
          base: `calc(${viewportHeight} - ${VERTICAL_DRAWER_PADDING_PIXELS}px)`,
        }}
        overflow="auto"
        pb={{ desktop: 700 }}
        pt={{ base: 400, tablet: 'unset' }}
        px={{ desktop: 500 }}
      >
        <UploadImageBox
          alignSelf={{ base: 'center', tablet: 'unset' }}
          borderRadius={400}
          height="100%"
          imageUrl={imageUrl}
          maxHeight={{ base: '240px', desktop: '575px' }}
          minHeight={{ base: 600, desktop: 750 }}
        />
        <Box
          data-testid="upload-image-form-wrapper"
          flex={1}
          flexDirection="column"
          maxWidth="100%"
        >
          <Heading
            alignSelf={{
              base: 'flex-start',
              tablet: 'center',
              desktop: 'flex-start !important',
            }}
            fontWeight={700}
            m="0 !important"
            maxW={{ base: '280px', tablet: 750, desktop: '440px' }}
            pb={{ base: 500, tablet: 700, desktop: 600 }}
            pt={{ base: 600, tablet: 800, desktop: 'unset' }}
            size={{ base: 500, tablet: 700 }}
            textAlign={{ base: 'left', tablet: 'center', desktop: 'left' }}
          >
            {t('uploadImage.whatTypeOfSpace')}
          </Heading>
          {spaceTypeSelectorComponent || (
            <SpaceTypeSelector
              alignSelf={{ base: 'center', desktop: 'flex-start' }}
              setSpaceType={setSpaceType}
              spaceType={spaceType}
              spaceTypes={spaceTypes}
              width={{ tablet: 850, desktop: 800 }}
            />
          )}
          <Box
            alignItems={{
              base: 'flex-start',
              tablet: 'center',
              desktop: 'flex-start',
            }}
            data-testid="upload-submit-wrapper"
            flexDirection="column"
            gap={{ base: 400, tablet: 600, desktop: 600 }}
            justifyContent={{ base: 'flex-end', desktop: 'flex-start' }}
            minWidth="100%"
            pb={{ base: 400, desktop: 'unset' }}
            pt={{ base: 400, tablet: 600, desktop: 600 }}
          >
            {consentMandatory && (
              <Flex flex={1} flexDirection="column" justifyContent="flex-end">
                <Checkbox
                  alignItems="flex-start"
                  checked={agreedToTerms}
                  marginLeft={100}
                  onChange={e => {
                    setAgreedToTerms(e.target.checked);
                  }}
                >
                  <Body
                    color="textLight"
                    marginBottom={0}
                    marginTop={0}
                    mr={400}
                    paddingTop={0}
                    size={{ base: 300, tablet: 400 }}
                  >
                    {legalText || (
                      <Trans
                        components={{
                          SupportEmailLink: (
                            <LinkExternal
                              color="inherit"
                              href={External.SUPPORT_EMAIL.path}
                            />
                          ),
                        }}
                        i18nKey="uploadImage.embedLegalText"
                        t={t}
                      />
                    )}
                  </Body>
                </Checkbox>
              </Flex>
            )}
            <Button
              data-testid="upload-image-submit-button"
              height={500}
              isDisabled={canContinue}
              isLoading={isLoading}
              mb={{ base: 50, tablet: 'unset', desktop: 'unset' }}
              minWidth={{ base: '100%', tablet: '195px' }}
              mt={{ tablet: 200, desktop: 'unset' }}
              onClick={onNext}
              optionalTrackingProperties={{
                ...trackingData,
                button_location: 'bottom-main-page',
                track_id: 'tos-generate-images',
                primary_cta: true,
              }}
            >
              {ctaText}
            </Button>
          </Box>
        </Box>
      </Box>
    </Sheet.Contents>
  );
};
